import { Box, CircularProgress, Typography } from '@mui/material';
import DrawerMenu from 'components/DrawerMenu';
import ScrollableTable from 'components/ScrollableTable';
import strings from 'common/Translation/Translate';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';

export type OnboardingRecordsItemKey =
  | 'eventTypes'
  | 'venues'
  | 'packages'
  | 'addons'
  | 'seatingStyles'
  | 'venueConfigurations';

type RecordsColumn = {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
};

type RecordsViewConfig = {
  listApi: string;
  defaultSort: string[];
  columns: RecordsColumn[];
};

const RECORDS_VIEW_CONFIG: Record<OnboardingRecordsItemKey, RecordsViewConfig> = {
  eventTypes: {
    listApi: URLS.eventType,
    defaultSort: ['createdAt:desc'],
    columns: [{ key: 'eventTypeName', label: strings.segment_title_list }],
  },
  venues: {
    listApi: URLS.venue,
    defaultSort: ['sortOrder:asc'],
    columns: [
      { key: 'venueName', label: strings.venue_column_name },
      { key: 'available', label: strings.venue_column_available, align: 'center' },
    ],
  },
  packages: {
    listApi: URLS.pricePackage,
    defaultSort: ['pricePackageOrder:asc'],
    columns: [
      { key: 'pricePackageName', label: strings.pricePackage_column_name },
      { key: 'pricePackageOrder', label: strings.pricePackage_column_Price_order, align: 'center' },
    ],
  },
  addons: {
    listApi: URLS.addOns,
    defaultSort: ['createdAt:desc'],
    columns: [
      { key: 'addOnName', label: strings.addOn_text_name },
      { key: 'addOnType', label: strings.addOn_column_type },
      { key: 'formattedPrice', label: strings.addOn_column_price, align: 'right' },
    ],
  },
  seatingStyles: {
    listApi: URLS.layout,
    defaultSort: ['createdAt:desc'],
    columns: [{ key: 'layoutName', label: strings.layout_column_name }],
  },
  venueConfigurations: {
    listApi: URLS.venueConfiguration,
    defaultSort: ['createdAt:desc'],
    columns: [
      { key: 'venueName', label: strings.venueConfig_column_venue },
      { key: 'eventTypeName', label: strings.venueConfig_column_segment },
      { key: 'available', label: strings.venueConfig_column_available, align: 'center' },
      { key: 'completed', label: strings.venueConfig_column_health, align: 'center' },
    ],
  },
};

const formatCellValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? strings.dropdown_yes_text : strings.dropdown_no_text;
  }
  if (key === 'completed' && typeof value === 'number') {
    return `${Math.round(value)}%`;
  }
  return String(value);
};

type PropertyOnboardingRecordsDrawerProps = {
  open: boolean;
  itemKey: OnboardingRecordsItemKey | null;
  title: string;
  onClose: () => void;
};

const PropertyOnboardingRecordsDrawer: FC<PropertyOnboardingRecordsDrawerProps> = ({
  open,
  itemKey,
  title,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<any[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const config = itemKey ? RECORDS_VIEW_CONFIG[itemKey] : null;

  const fetchRecords = useCallback(async () => {
    if (!config) return;

    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.post(config.listApi, {
        page: 1,
        size: 500,
        filter: [],
        sort: config.defaultSort,
      });

      const content = Array.isArray(data?.content) ? data.content : [];
      setRecords(content);
      setTotalElements(data?.totalElements ?? content.length);
    } catch {
      setRecords([]);
      setTotalElements(0);
      setError(strings.property_onboarding_records_load_error);
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    if (open && itemKey) {
      setRecords([]);
      setTotalElements(0);
      fetchRecords();
    }
  }, [open, itemKey, fetchRecords]);

  const columns = useMemo(() => config?.columns ?? [], [config]);

  const subtitle = useMemo(() => {
    if (loading) return '';
    if (totalElements === 0) return strings.property_onboarding_records_drawer_empty;
    if (records.length < totalElements) {
      return strings.property_onboarding_records_showing
        .replace('{shown}', String(records.length))
        .replace('{total}', String(totalElements));
    }
    return strings.property_onboarding_records_count.replace('{count}', String(totalElements));
  }, [loading, records.length, totalElements]);

  return (
    <DrawerMenu open={open} handleClose={onClose} closeButton title={title} width="min(720px, 92vw)" flexColumn>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 1.5 }}>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 200 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ScrollableTable
              columns={columns}
              data={records}
              maxHeight="100%"
              emptyMessage={strings.property_onboarding_records_drawer_empty}
              getRowKey={(row, index) => row?.id ?? index}
              renderCell={(column, row) => (
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.4 }}>
                  {formatCellValue(column.key ?? '', column.key ? row[column.key] : null)}
                </Typography>
              )}
            />
          </Box>
        )}
      </Box>
    </DrawerMenu>
  );
};

export default PropertyOnboardingRecordsDrawer;
